import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { BlogPost } from '../types';

interface MarkdownEditorProps {
  onContentChange?: (content: string) => void;
  placeholder?: string;
  initialContent?: string;
  title?: string;
  onTitleChange?: (title: string) => void;
  onSubtitleChange?: (subtitle: string) => void;
  onStatusChange?: (status: string) => void;
  post?: BlogPost;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  onContentChange,
  placeholder = "Write your story...",
  initialContent = "",
  title = "",
  onTitleChange,
  onSubtitleChange,
  onStatusChange,
  post
}) => {
  const [content, setContent] = useState(initialContent);
  const [viewMode, setViewMode] = useState<'preview' | 'metadata'>('preview');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update content when initialContent changes (for editing existing posts)
  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  const handleContentChange = useCallback((value: string) => {
    setContent(value);
    onContentChange?.(value);
  }, [onContentChange]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleContentChange(e.target.value);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onTitleChange?.(e.target.value);
  };

  const handleMetadataTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onTitleChange?.(e.target.value);
  };

  const handleSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSubtitleChange?.(e.target.value);
  };

  const handleStatusSelect = (value: string) => {
    onStatusChange?.(value);
    setStatusDropdownOpen(false);
  };

  const toggleStatusDropdown = () => {
    setStatusDropdownOpen(!statusDropdownOpen);
  };

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
    { value: 'retracted', label: 'Retracted' }
  ];

  // Determine current status based on post data
  const getCurrentStatus = () => {
    // If post has explicit status, use that
    if (post?.status) {
      return post.status;
    }
    // Fall back to legacy logic: posted_at means published
    if (post?.posted_at) {
      return 'published';
    }
    return 'draft'; // Default to draft
  };

  // Get current status label
  const getCurrentStatusLabel = () => {
    const currentStatus = getCurrentStatus();
    return statusOptions.find(option => option.value === currentStatus)?.label || 'Draft';
  };

  // Focus management
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  return (
    <div className="markdown-editor">
      <div className="editor-header">
        <div className="title-section">
          <input
            type="text"
            className="editor-title-input"
            value={title}
            onChange={handleTitleChange}
            placeholder="Enter title..."
          />
        </div>
        <div className="editor-controls">
          <button
            className={`control-btn ${viewMode === 'preview' ? 'active' : ''}`}
            onClick={() => setViewMode('preview')}
          >
            Preview
          </button>
          <button
            className={`control-btn ${viewMode === 'metadata' ? 'active' : ''}`}
            onClick={() => setViewMode('metadata')}
          >
            Metadata
          </button>
        </div>
      </div>
      
      <div className="editor-content">
        <div className="live-editor-container">
          <div className="live-editor-pane">
            <textarea
              ref={textareaRef}
              className="live-textarea"
              value={content}
              onChange={handleTextareaChange}
              placeholder={placeholder}
              spellCheck={false}
            />
          </div>

          <div className="live-preview-pane">
            {viewMode === 'preview' && (
              <div className="live-preview-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                >
                  {content || "*Nothing to preview*"}
                </ReactMarkdown>
              </div>
            )}

            {viewMode === 'metadata' && (
              <div className="metadata-content">
                <div className="metadata-section">
                  <h3>Document Settings</h3>
                  <div className="metadata-field metadata-field-editable">
                    <label>Title:</label>
                    <input
                      type="text"
                      className="metadata-input"
                      value={title}
                      onChange={handleMetadataTitleChange}
                      placeholder="Enter title..."
                    />
                  </div>
                  <div className="metadata-field metadata-field-editable">
                    <label>Subtitle:</label>
                    <input
                      type="text"
                      className="metadata-input"
                      value={post?.subtitle || ''}
                      onChange={handleSubtitleChange}
                      placeholder="Enter subtitle..."
                    />
                  </div>
                  <div className="metadata-field metadata-field-editable">
                    <label>Status:</label>
                    <div className={`custom-select ${statusDropdownOpen ? 'open' : ''}`} onClick={toggleStatusDropdown}>
                      <div className="custom-select-value">
                        {getCurrentStatusLabel()}
                      </div>
                      <div className="custom-select-arrow">
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      {statusDropdownOpen && (
                        <div className="custom-select-dropdown" onClick={(e) => e.stopPropagation()}>
                          {statusOptions.map(option => (
                            <div
                              key={option.value}
                              className={`custom-select-option ${getCurrentStatus() === option.value ? 'selected' : ''}`}
                              onClick={() => handleStatusSelect(option.value)}
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="metadata-field">
                    <label>Slug:</label>
                    <span>{post?.slug || 'Not set'}</span>
                  </div>
                  <div className="metadata-field">
                    <label>Author:</label>
                    <span>{post?.author_username || 'Unknown'}</span>
                  </div>
                  <div className="metadata-field">
                    <label>Character Count:</label>
                    <span>{content.length}</span>
                  </div>
                  <div className="metadata-field">
                    <label>Word Count:</label>
                    <span>{content.trim().split(/\s+/).filter(word => word.length > 0).length}</span>
                  </div>
                  {post?.posted_at && (
                    <div className="metadata-field">
                      <label>Published:</label>
                      <span>{new Date(post.posted_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkdownEditor;